import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Button, Modal, EMOJI_COLOR_CLASSES } from '@/components/ui';
import { useAppShell, useAuth, useUserRole } from '@/hooks';
import { toast } from '@/stores';
import { householdService, profileImageService } from '@/services';
import { ChangeProfileImage } from '@/components/domain/shared/ChangeProfileImage';
import { ProfileAvatar } from '@/components/domain/shared/ProfileAvatar';
import { SelectEmoji } from '@/components/domain/shared/SelectEmoji';
import { DEFAULT_HOUSEHOLD_EMOJI } from '@/constants/household';
import { nameError, normalizeName } from '@/lib/nameField';
import { SettingsSubPage } from './SettingsSubPage';
import { SettingsHouseholdSkeleton } from './skeletons';

export function SettingsHousehold() {
  const navigate = useNavigate();
  const { isPrimary } = useUserRole();
  const { householdId, householdResolved, refreshHousehold } = useAppShell();
  const { signOut } = useAuth();

  // Household data
  const [name, setName] = useState('');
  const [savedName, setSavedName] = useState('');

  // Emoji avatar
  const [emoji, setEmoji] = useState(DEFAULT_HOUSEHOLD_EMOJI);
  const [savedEmoji, setSavedEmoji] = useState(DEFAULT_HOUSEHOLD_EMOJI);
  const [emojiColor, setEmojiColor] = useState<string>(EMOJI_COLOR_CLASSES[0]);
  const [savedEmojiColor, setSavedEmojiColor] = useState<string>(
    EMOJI_COLOR_CLASSES[0],
  );
  const [imagePath, setImagePath] = useState<string | null>(null);

  // Sub-screen visibility
  const [showChangeImage, setShowChangeImage] = useState(false);
  const [showSelectEmoji, setShowSelectEmoji] = useState(false);

  // Leave household modal
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  // Loading / save state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const nameInvalid = nameError(name, 'Household name');
  // Only once the owner has changed it — see SettingsProfile.
  const shownNameError = name !== savedName ? nameInvalid : null;

  const isDirty =
    name !== savedName ||
    emoji !== savedEmoji ||
    emojiColor !== savedEmojiColor;

  // Load household when householdId changes
  useEffect(() => {
    if (!householdId) {
      // Only stop showing the skeleton once resolution has FINISHED.
      // Otherwise the first paint (id still pending) renders this page's
      // empty/default state before the real data can possibly arrive.
      if (householdResolved) setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const household = await householdService.getHousehold(householdId);
        if (cancelled) return;
        if (household) {
          setName(household.name);
          setSavedName(household.name);
          if (household.emoji_avatar) {
            setEmoji(household.emoji_avatar);
            setSavedEmoji(household.emoji_avatar);
          }
          if (household.emoji_color) {
            setEmojiColor(household.emoji_color);
            setSavedEmojiColor(household.emoji_color);
          }
          setImagePath(household.avatar_image_path ?? null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [householdId, householdResolved]);

  const handleSave = async () => {
    if (!householdId || !isDirty || nameInvalid) return;
    const cleanName = normalizeName(name);
    setSaving(true);
    try {
      await householdService.updateHousehold(householdId, {
        name: cleanName,
        emoji_avatar: emoji,
        emoji_color: emojiColor,
      });
      setName(cleanName);
      setSavedName(cleanName);
      setSavedEmoji(emoji);
      setSavedEmojiColor(emojiColor);
      await refreshHousehold?.();
      toast.success('Household updated');
    } catch (err) {
      console.error('Failed to update household:', err);
      toast.error('Failed to update household');
    } finally {
      setSaving(false);
    }
  };

  const handleEmojiSave = (newEmoji: string, newColor: string) => {
    setEmoji(newEmoji);
    setEmojiColor(newColor);
    setShowSelectEmoji(false);
    setShowChangeImage(false);
  };

  const handleUploadImage = async (file: File) => {
    if (!householdId) throw new Error('Household not loaded');
    const { avatar_image_path } = await profileImageService.uploadAvatarImage(
      'household',
      householdId,
      file,
    );
    setImagePath(avatar_image_path);
    setShowChangeImage(false);
    await refreshHousehold?.();
    toast.success('Household photo updated');
  };

  const handleRemoveImage = async () => {
    if (!householdId) throw new Error('Household not loaded');
    await profileImageService.removeAvatarImage('household', householdId);
    setImagePath(null);
    setShowChangeImage(false);
    await refreshHousehold?.();
    toast.success('Household photo removed');
  };

  const handleLeave = async () => {
    if (!householdId) return;
    try {
      await householdService.leaveHousehold(householdId);
      setShowLeaveModal(false);
      // YAR-501: leaving the ONLY household left the member signed in to an
      // empty app. With nothing left to show, sign them out (agreed in the
      // daily) instead of re-resolving onto nothing.
      const remaining = await householdService
        .getMyHouseholdsForCurrentUser()
        .catch(() => null);
      if (remaining && remaining.length === 0) {
        toast.success('Left household');
        await signOut();
        navigate('/', { replace: true });
        return;
      }
      // The household we just left still EXISTS — RLS only hides its data — so
      // the shell's stale-recovery (which fires only when getHousehold returns
      // null) won't switch us off it. Re-resolve so we land on another
      // household we still belong to — unified household resolution
      // (getOldestMembershipForUser) picks the oldest remaining membership.
      // The active-household pin may still name the household we left; the
      // resolver ignores a pin without an active membership, so it falls back.
      await refreshHousehold?.();
      toast.success('Left household');
      navigate('/settings');
    } catch (err) {
      console.error('Failed to leave household:', err);
      toast.error('Failed to leave household');
      setShowLeaveModal(false);
    }
  };

  return (
    <>
      <SettingsSubPage
        title="Household Profile"
        onBack={() => navigate('/settings')}
        stickyFooter={
          <div className="flex flex-col gap-3">
            {/* `size="lg"` is the design system's 20px step (`text-lg`).
                The default `md` renders the label at 16px, which QA filed
                against Figma on YAR-230. */}
            {isPrimary && (
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={handleSave}
                disabled={!isDirty || !!nameInvalid || saving}
                loading={saving}
              >
                Save changes
              </Button>
            )}
            {!isPrimary && (
              // `destructiveOutline` carries Figma's #a82222 on the border and
              // the label. The `border-danger text-danger` overrides it used
              // to stack on `outline` lost to the variant's own colours on
              // stylesheet order, so it rendered #264133 (YAR-145).
              <Button
                variant="destructiveOutline"
                size="lg"
                fullWidth
                onClick={() => setShowLeaveModal(true)}
              >
                Leave household
              </Button>
            )}
          </div>
        }
      >
        {loading ? (
          <SettingsHouseholdSkeleton />
        ) : (
          <div className="ph-no-capture flex flex-col items-center gap-6 px-5 py-6">
            {/* Emoji avatar — only the household owner can change it. */}
            <div className="flex flex-col items-center gap-2">
              {isPrimary ? (
                <>
                  <button
                    type="button"
                    onClick={() => setShowChangeImage(true)}
                    className="cursor-pointer rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    aria-label="Change household image"
                  >
                    <ProfileAvatar
                      imagePath={imagePath}
                      emoji={emoji}
                      color={emojiColor}
                      size="2xl"
                    />
                  </button>
                  <span className="text-sm text-muted-foreground">
                    Tap to change image
                  </span>
                </>
              ) : (
                <ProfileAvatar
                  imagePath={imagePath}
                  emoji={emoji}
                  color={emojiColor}
                  size="2xl"
                />
              )}
            </div>

            {/* Household name */}
            <div className="w-full">
              <Input
                label="Household name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!isPrimary}
                error={shownNameError ?? undefined}
              />
            </div>
          </div>
        )}
      </SettingsSubPage>

      {/* ChangeProfileImage sub-screen */}
      {showChangeImage && (
        <ChangeProfileImage
          onBack={() => setShowChangeImage(false)}
          onChooseEmoji={() => {
            setShowChangeImage(false);
            setShowSelectEmoji(true);
          }}
          onUpload={isPrimary ? handleUploadImage : undefined}
          onRemove={isPrimary ? handleRemoveImage : undefined}
          hasImage={!!imagePath}
          headerVariant="subpage"
          preview={{
            imagePath,
            emoji,
            color: emojiColor,
          }}
        />
      )}

      {/* SelectEmoji sub-screen */}
      {showSelectEmoji && (
        <SelectEmoji
          currentEmoji={emoji}
          currentColor={emojiColor}
          onSave={handleEmojiSave}
          onBack={() => {
            setShowSelectEmoji(false);
            setShowChangeImage(true);
          }}
          headerVariant="subpage"
        />
      )}

      {/* Leave household confirmation modal. Layout and tones follow Figma
          (Settings / household profile / leave household, YAR-145): no
          header bar or ✕, a centred title naming the household, the red
          outline CTA and a plain Cancel — the same shape as Delete account's
          confirmation. The body line is kept so the copy still says that
          leaving removes access (AC8); whether Figma's title alone is enough
          is with @kelvin. */}
      <Modal
        open={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        ariaLabel="Leave household"
        bare
      >
        <div className="ph-no-capture flex flex-col gap-4 items-center p-5">
          <p className="text-xl font-medium text-text-heading text-center w-full break-words">
            {savedName
              ? `Are you sure you want to leave ${savedName}?`
              : 'Are you sure you want to leave this household?'}
          </p>
          <p className="text-sm text-text-body text-center leading-5">
            You'll lose access to all shared data in this household.
          </p>
          <div className="flex flex-col gap-3 w-full">
            <Button
              variant="destructiveOutline"
              fullWidth
              onClick={handleLeave}
            >
              Leave household
            </Button>
            <Button
              variant="tertiary"
              fullWidth
              onClick={() => setShowLeaveModal(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
